// Polyfills first (must load before app code)
import 'react-app-polyfill/ie11';
import 'react-app-polyfill/stable';
import 'intersection-observer';
import ResizeObserver from 'resize-observer-polyfill';

import React from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import './index.css';
import './firebase';
import App from './App';
import { AuthProvider } from './contexts/AuthContext';
import { ToastProvider } from './contexts/ToastContext';
import { CustomThemeProvider } from './contexts/ThemeContext';
import ErrorBoundary from './components/common/ErrorBoundary';
import AnalyticsRouteTracker from './components/common/AnalyticsRouteTracker';

if (typeof window !== 'undefined' && typeof (window as any).ResizeObserver === 'undefined') {
  (window as any).ResizeObserver = ResizeObserver;
}

// Browser compatibility logging (development only)
const logBrowserInfo = () => {
  if (process.env.NODE_ENV === 'development' && typeof window !== 'undefined') {
    console.log('=== Browser Compatibility Check ===');
    console.log('User Agent:', navigator.userAgent);
    console.log('Platform:', navigator.platform);
    console.log('React Version:', React.version);
    console.log('createRoot Available:', typeof ReactDOM.createRoot === 'function');
    console.log('=====================================');
  }
};

// Log browser info on load (development only)
logBrowserInfo();

// Detect if browser supports createRoot
const supportsCreateRoot = typeof ReactDOM.createRoot === 'function';

const rootElement = document.getElementById('root') as HTMLElement;

if (!rootElement) {
  console.error('❌ Root element not found!');
  document.body.innerHTML = '<div style="padding: 20px; text-align: center;"><h2>Application Error</h2><p>Root element not found. Please check the HTML structure.</p></div>';
} else {
  const AppComponent = (
    <ErrorBoundary>
      <React.StrictMode>
        <BrowserRouter>
          <AnalyticsRouteTracker />
          <CustomThemeProvider>
            <ToastProvider>
              <AuthProvider>
                <App />
              </AuthProvider>
            </ToastProvider>
          </CustomThemeProvider>
        </BrowserRouter>
      </React.StrictMode>
    </ErrorBoundary>
  );

  if (supportsCreateRoot) {
    // Modern browsers (React 18)
    try {
      const root = ReactDOM.createRoot(rootElement);
      root.render(AppComponent);
      if (process.env.NODE_ENV === 'development') {
        console.log('✅ Using React 18 createRoot API');
      }
    } catch (error) {
      console.error('❌ Error using createRoot, falling back to legacy render:', error);
      // Fallback to legacy render
      const ReactDOMLegacy = require('react-dom');
      ReactDOMLegacy.render(AppComponent, rootElement);
    }
  } else {
    // Legacy browsers (old iPads) - use React 17 API
    if (process.env.NODE_ENV === 'development') {
      console.log('⚠️ Using legacy ReactDOM.render (createRoot not available)');
    }
    try {
      const ReactDOMLegacy = require('react-dom');
      ReactDOMLegacy.render(AppComponent, rootElement);
    } catch (error) {
      console.error('❌ Error rendering with legacy API:', error);
      rootElement.innerHTML = `
        <div style="padding: 20px; text-align: center; font-family: Arial, sans-serif;">
          <h2 style="color: #d32f2f;">Application Error</h2>
          <p>Failed to initialize the application. Your browser may not be supported.</p>
          <p style="color: #666; font-size: 0.9em; margin-top: 20px;">
            Error: ${error instanceof Error ? error.message : 'Unknown error'}
          </p>
          <p style="color: #666; font-size: 0.9em;">
            Please check the browser console for more details.
          </p>
        </div>
      `;
    }
  }
}